import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Shield, FileText, Sparkles, RefreshCw, Trash2,
  LogOut, Mail, ChevronRight, Loader2, Check, Bell, KeyRound, X,
  Gift, Copy, Share2, HeartHandshake,
} from "lucide-react";
import { useSubscription } from "../context/SubscriptionContext";
import TitleBar from "../components/TitleBar";
import { devClearSubscription } from "../lib/iap";
import { notificationsEnabled, requestPermission, setNotificationsEnabled, cancelStreakReminder, scheduleCheckinReminder, cancelCheckinReminder } from "../lib/notifications";
import { api } from "../lib/api";
import { getPendingRef, clearPendingRef } from "../lib/referral";
import FounderAnalyticsStrip from "../components/FounderAnalyticsStrip";
import RehabPanel from "../components/RehabPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ to, href, onClick, icon: Icon, label, sub, danger, rightSlot, testid }) => {
  const inner = (
    <div className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border ${danger ? "border-red-500/20 bg-red-500/5" : "border-zinc-800 bg-zinc-900/60"} hover:border-zinc-700 transition-all`}>
      <div className={`h-9 w-9 rounded-xl grid place-items-center ${danger ? "bg-red-500/10 text-red-300" : "bg-zinc-800 text-zinc-200"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${danger ? "text-red-300" : "text-white"}`}>{label}</div>
        {sub && <div className="text-[11px] text-zinc-400 mt-0.5 leading-tight">{sub}</div>}
      </div>
      {rightSlot}
      {(to || href) && !rightSlot && <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
    </div>
  );
  if (to) return <Link to={to} data-testid={testid}>{inner}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid}>{inner}</a>;
  return <button onClick={onClick} className="w-full text-left" data-testid={testid}>{inner}</button>;
};

export default function Settings() {
  const { isPro, refresh, restore } = useSubscription();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [notifOn, setNotifOn] = useState(notificationsEnabled());
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState("");

  // ----- Referral state -----
  const [refData, setRefData] = useState(null);
  const [refCopied, setRefCopied] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");

  // ----- Streak protection state -----
  const [streakInfo, setStreakInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    api.streakGet()
      .then((d) => { if (alive) setStreakInfo(d); })
      .catch(() => {});
    const onUpdate = (e) => setStreakInfo((prev) => ({ ...(prev || {}), ...(e.detail || {}) }));
    window.addEventListener('streak:updated', onUpdate);
    return () => {
      alive = false;
      window.removeEventListener('streak:updated', onUpdate);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    api.myReferralCode()
      .then((d) => { if (alive) setRefData(d); })
      .catch(() => {});
    // Pre-fill the redeem input from URL or localStorage (?ref=XXXXXX flow)
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = (params.get("ref") || "").trim().toUpperCase();
      const pending = fromUrl || getPendingRef();
      if (pending) setRedeemCode(pending);
    } catch { /* ignore */ }
    return () => { alive = false; };
  }, []);

  // When linked from /my-plan with hash like "#referral", scroll the section
  // into view so newly-subscribed users land directly on the referral block.
  useEffect(() => {
    const hash = (window.location.hash || "").replace("#", "");
    if (!hash) return;
    // Defer until the section has rendered.
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, []);

  const onCopyRef = async () => {
    if (!refData?.code) return;
    try {
      await navigator.clipboard.writeText(refData.code);
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 1800);
    } catch {/* ignore */}
  };

  const onShareRef = async () => {
    if (!refData?.code) return;
    const text = `Get a free month of FreeFitFuel — use my code ${refData.code} at signup.`;
    const url = `${window.location.origin}/?ref=${refData.code}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "FreeFitFuel", text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        setRefCopied(true);
        setTimeout(() => setRefCopied(false), 1800);
      }
    } catch {/* user dismissed */}
  };

  const onRedeem = async () => {
    const code = redeemCode.trim().toUpperCase();
    if (!code) { setRedeemMsg("Enter a code."); return; }
    setRedeemBusy(true);
    setRedeemMsg("");
    try {
      const r = await api.redeemReferralCode(code);
      setRedeemMsg(r.message || `Done. You got ${r.friend_days} days free.`);
      setRedeemCode("");
      clearPendingRef();
      await refresh();
    } catch (e) {
      setRedeemMsg(e?.response?.data?.detail || "Couldn't redeem that code.");
    } finally { setRedeemBusy(false); }
  };

  const onRecover = async () => {
    const email = recoverEmail.trim();
    if (!email || !email.includes("@")) {
      setRecoverMsg("Enter a valid email.");
      return;
    }
    setRecoverBusy(true);
    setRecoverMsg("");
    try {
      const res = await api.recoverByEmail(email);
      setRecoverMsg(res.message || "Entitlement restored. Refresh the app.");
      await refresh();
    } catch (e) {
      setRecoverMsg(e?.response?.data?.detail || "Recovery failed. Try again.");
    } finally {
      setRecoverBusy(false);
    }
  };

  const toggleNotifications = async () => {
    if (notifOn) {
      setNotificationsEnabled(false);
      setNotifOn(false);
      await cancelStreakReminder();
      await cancelCheckinReminder();
      setMsg("Reminders turned off.");
    } else {
      const status = await requestPermission();
      const granted = status === "granted";
      setNotifOn(granted);
      if (granted) {
        try { await scheduleCheckinReminder(); } catch { /* ignore — reminder is best-effort */ }
      }
      setMsg(granted ? "Reminders on. Streak nudge at 8pm; weekly check-in Sundays at 6pm." : "Permission denied. Enable notifications in your phone's Settings → FreeFitFuel.");
    }
  };

  const onRestore = async () => {
    setBusy(true); setMsg("");
    const ok = await restore();
    setBusy(false);
    setMsg(ok ? "Subscription restored." : "No active subscription found.");
  };

  const onResetOnboarding = () => {
    if (!window.confirm("Reset onboarding? Your profile will be cleared but your plan stays.")) return;
    localStorage.removeItem("fff_onboarded");
    localStorage.removeItem("fff_onboarding");
    localStorage.removeItem("fff_preview_seen");
    window.location.href = "/my-plan";
  };

  const onManageSubscription = async () => {
    const email = window.prompt("Enter the email you paid with — we'll open the Stripe billing portal so you can manage or cancel.");
    if (!email) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/payments/billing-portal`, {
        email: email.trim(),
        return_url: window.location.href,
      });
      if (r?.data?.url) {
        window.location.href = r.data.url;
      } else {
        setMsg("Couldn't open the billing portal. Try again shortly.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong. Try again shortly.";
      setMsg(detail);
    } finally {
      setBusy(false);
    }
  };

  const onClearLocal = () => {
    if (!window.confirm("Clear all local app data? This signs you out on this device.")) return;
    devClearSubscription();
    localStorage.clear();
    window.location.href = "/";
  };

  const onResetStreak = async () => {
    if (!window.confirm("Reset streak to zero? Your lifetime days-active total is preserved. Use this if the counter drifted from what you actually did.")) return;
    try {
      await api.streakReset();
      setMsg("Streak reset. From here, only real workouts, lifts and check-ins increment.");
    } catch {
      setMsg("Couldn't reach the server. Try again shortly.");
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0b0b0c] text-white"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="settings-screen"
    >
      <TitleBar title="Settings" back="/my-plan" showStreak={false} showSettings={false} />
      <div className="mx-auto max-w-md px-5 py-6 pb-32">
        <div className="mb-5">
          <p className="text-[10px] font-mono uppercase tracking-[0.22em] font-black text-[#BEFF38]">Account</p>
          <h1 className="mt-1 text-2xl font-black uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Settings</h1>
        </div>

        {/* Subscription */}
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1">Subscription</div>
        <div className="space-y-2 mb-6">
          <Row
            icon={isPro ? Check : Sparkles}
            label={isPro ? "FreeFitFuel Pro" : "Upgrade to Pro"}
            sub={isPro ? "Active — thanks for supporting the app" : "£5/mo · £12/3mo · £18/6mo · £19-25/yr"}
            to={isPro ? undefined : "/my-plan"}
            rightSlot={isPro ? <span className="text-[10px] px-2 py-1 rounded-full bg-lime-400/20 text-lime-300">Active</span> : undefined}
            testid="settings-subscription-row"
          />
          <Row icon={RefreshCw} label="Restore purchases" onClick={onRestore} sub="If you have an active subscription on another device" testid="settings-restore-btn" />
          <Row icon={KeyRound} label="Already paid? Recover by email" onClick={() => { setRecoverOpen(true); setRecoverMsg(""); }} sub="Re-link a Stripe purchase to this device" testid="settings-recover-btn" />
          <Row icon={ChevronRight} label="Manage subscription" onClick={onManageSubscription} sub="Opens the Stripe billing portal — update card, pause or cancel" testid="settings-manage-link" />
          {busy && <div className="flex items-center gap-2 text-xs text-zinc-400 px-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…</div>}
          {msg && <div className="text-xs text-zinc-300 px-2">{msg}</div>}
        </div>

        {/* Founder-only traffic sources — self-gated, renders nothing for non-owners */}
        <FounderAnalyticsStrip />

        {/* Referrals */}
        <div id="referral" className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1 scroll-mt-20" data-testid="referral-section">Refer a friend</div>
        <div className="space-y-2 mb-6">
          <div className="rounded-2xl border border-[#BEFF38]/30 bg-gradient-to-br from-[#BEFF38]/10 to-zinc-900/60 p-4" data-testid="referral-share-card">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl grid place-items-center bg-[#BEFF38]/20 text-[#BEFF38] shrink-0">
                <Gift className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">Give 1 month · Get 2 months</div>
                <div className="text-[11px] text-zinc-400 mt-0.5 leading-snug">
                  Share your code. Your friend gets <span className="text-[#BEFF38] font-bold">{refData?.friend_reward_days ?? 30} days free</span>. When they redeem, you get <span className="text-[#BEFF38] font-bold">{refData?.referrer_reward_days ?? 60} days free</span>.
                </div>
              </div>
            </div>

            {refData?.code && (
              <>
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex-1 rounded-lg bg-[#0b0c10] border border-zinc-800 px-3 py-2.5 font-mono text-lg tracking-[0.25em] text-[#BEFF38] text-center font-bold" data-testid="referral-code-display">
                    {refData.code}
                  </div>
                  <button
                    onClick={onCopyRef}
                    data-testid="referral-copy-btn"
                    aria-label="Copy code"
                    className="h-11 w-11 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white grid place-items-center transition-colors"
                  >
                    {refCopied ? <Check className="h-4 w-4 text-[#BEFF38]" /> : <Copy className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={onShareRef}
                    data-testid="referral-share-btn"
                    aria-label="Share code"
                    className="h-11 w-11 rounded-lg bg-[#BEFF38] hover:bg-lime-300 text-black grid place-items-center transition-colors"
                  >
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 text-[10.5px] text-zinc-500">
                  {refData.redemption_count > 0
                    ? `${refData.redemption_count} friend${refData.redemption_count === 1 ? '' : 's'} redeemed — you've earned ${refData.redemption_count * (refData.referrer_reward_days ?? 60)} days free.`
                    : "No redemptions yet — share the code with someone who'll actually train."}
                </p>
              </>
            )}
          </div>

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4" data-testid="referral-redeem-card">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl grid place-items-center bg-zinc-800 text-zinc-200 shrink-0">
                <Gift className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">Got a code?</div>
                <div className="text-[11px] text-zinc-400 mt-0.5">Redeem a friend&apos;s code for {refData?.friend_reward_days ?? 30} days free.</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <input
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={8}
                data-testid="referral-redeem-input"
                className="flex-1 px-3 py-2.5 bg-[#0b0c10] border border-zinc-800 focus:border-[#BEFF38] focus:ring-2 focus:ring-[#BEFF38]/20 rounded-lg text-white outline-none text-sm font-mono tracking-widest text-center"
              />
              <button
                onClick={onRedeem}
                disabled={redeemBusy || !redeemCode.trim()}
                data-testid="referral-redeem-btn"
                className="px-4 h-11 rounded-lg bg-[#ff7a1a] hover:bg-[#ff8a33] text-black font-bold text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {redeemBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={3} />}
                Redeem
              </button>
            </div>
            {redeemMsg && <p className="mt-2 text-[11px] text-zinc-300" data-testid="referral-redeem-msg">{redeemMsg}</p>}
          </div>
        </div>

        {/* Notifications */}
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1">Notifications</div>
        <div className="space-y-2 mb-6">
          <Row
            icon={Bell}
            label="Streak reminders"
            sub={notifOn ? "On — we'll nudge you at 8pm if your flame is at risk" : "Off — turn on to protect your streak"}
            onClick={toggleNotifications}
            rightSlot={
              <div className={`h-6 w-11 rounded-full p-0.5 transition-colors ${notifOn ? "bg-lime-400" : "bg-zinc-700"}`}>
                <div className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${notifOn ? "translate-x-5" : "translate-x-0"}`} />
              </div>
            }
            testid="settings-notifications-toggle"
          />
        </div>

        {/* Legal */}
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1">Legal</div>
        <div className="space-y-2 mb-6">
          <Row icon={Shield} label="Privacy Policy" to="/privacy" testid="settings-privacy-link" />
          <Row icon={FileText} label="Terms of Service" to="/terms" testid="settings-terms-link" />
          <Row icon={Mail} label="Contact support" href="mailto:gcanth@gmail.com" testid="settings-contact-link" />
        </div>

        {/* Rehab modules */}
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1">Rehab</div>
        <div className="mb-6">
          <RehabPanel testid="settings-rehab-panel" />
        </div>

        {/* App */}
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 px-1">App</div>
        <div className="space-y-2 mb-6">
          {streakInfo && (
            <div
              data-testid="settings-streak-pass-row"
              className="flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-zinc-800 bg-zinc-900/60"
            >
              <div className={`h-9 w-9 rounded-xl grid place-items-center ${streakInfo.pass_available ? 'bg-lime-500/10 text-lime-300' : 'bg-zinc-800 text-zinc-400'}`}>
                <HeartHandshake className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">Life-happens pass</div>
                <div className="text-[11px] text-zinc-400 mt-0.5 leading-tight">
                  {streakInfo.pass_available
                    ? "Available — one missed day forgiven per 30 days"
                    : `Refreshes in ${streakInfo.days_until_pass || 30} day${streakInfo.days_until_pass === 1 ? '' : 's'}`}
                </div>
              </div>
              <span className={`text-[11px] uppercase tracking-wider ${streakInfo.pass_available ? 'text-lime-300' : 'text-zinc-500'}`}>
                {streakInfo.pass_available ? 'Ready' : 'Used'}
              </span>
            </div>
          )}
          <Row icon={RefreshCw} label="Reset streak" onClick={onResetStreak} sub="Zero your streak counter. Lifetime days-active preserved." testid="settings-reset-streak-btn" />
          <Row icon={RefreshCw} label="Reset onboarding" onClick={onResetOnboarding} sub="Walk through goal, equipment and injuries again" testid="settings-reset-onboarding-btn" />
          <Row icon={Trash2} label="Clear all local data" onClick={onClearLocal} sub="Signs you out and resets local preferences" danger testid="settings-clear-data-btn" />
        </div>

        <div className="text-center text-[11px] text-zinc-600 mt-8">
          FreeFitFuel · v1.0.0
        </div>
      </div>

      {recoverOpen && (
        <div
          className="fixed inset-0 z-[10010] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setRecoverOpen(false)}
          data-testid="recover-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-6 text-white"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[#BEFF38] text-[11px] uppercase tracking-wider font-black">Recover purchase</p>
                <h3 className="mt-1 text-xl font-bold">Already paid? Recover by email</h3>
                <p className="text-sm text-zinc-400 mt-1">Enter the email you used to pay on Stripe. We&apos;ll link the purchase to this device.</p>
              </div>
              <button
                onClick={() => setRecoverOpen(false)}
                className="h-8 w-8 rounded-full bg-zinc-800 grid place-items-center hover:bg-zinc-700"
                data-testid="recover-close-btn"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="block mt-5 text-[11px] uppercase tracking-wider font-bold text-zinc-400">Email used at checkout</label>
            <input
              type="email"
              value={recoverEmail}
              onChange={(e) => setRecoverEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="recover-email-input"
              className="mt-2 w-full px-3 py-2.5 bg-[#0b0c10] border border-zinc-800 focus:border-[#BEFF38] focus:ring-2 focus:ring-[#BEFF38]/20 rounded-lg text-white outline-none text-sm"
            />

            {recoverMsg && (
              <p className="mt-3 text-xs text-zinc-300" data-testid="recover-msg">{recoverMsg}</p>
            )}

            <button
              onClick={onRecover}
              disabled={recoverBusy}
              data-testid="recover-submit-btn"
              className="mt-5 w-full rounded-full h-11 bg-[#BEFF38] text-black font-bold flex items-center justify-center gap-2 hover:bg-lime-300 disabled:opacity-60 transition-colors"
            >
              {recoverBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {recoverBusy ? "Looking up Stripe…" : "Recover entitlement"}
            </button>

            <p className="mt-3 text-[10.5px] text-zinc-500 leading-snug">
              We search Stripe for a successful checkout with that email and re-grant your Pro entitlement on this device. If you paid in the last minute, wait a moment then try again.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
